import { Box, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import Badge from "@mui/material/Badge";
import { styled } from "@mui/system";
import { withStyles } from "@material-ui/core";
import { formatColumnName } from "../utils";
import LinearProgress from "@material-ui/core/LinearProgress";
import MDTypography from "components/MDTypography";

// **** Components ****//

export const StyledBadge = styled(Badge)(({ theme }) => ({
    "& .MuiBadge-badge": {
        textWrap: "unwrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        textAlign: "center",
        height: "auto",
        padding: "6px",
    },
}));

export const StackSkeleton = () => {
    return (
        <Stack spacing={0.5}>
            <Skeleton variant="text" sx={{ fontSize: "1.5rem" }} />
            <Skeleton variant="text" sx={{ fontSize: "1.5rem" }} />
            <Skeleton variant="text" sx={{ fontSize: "1.5rem" }} />
            <Skeleton variant="text" sx={{ fontSize: "1.5rem" }} />
        </Stack>
    );
};

export function StyledProgress(pillbarColor){
    return withStyles({
        root: {
            height: 20, // Increase height of the progress bar here
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "0 10px", // Add padding for better visibility of percentage
            position: "relative", // Add position relative for absolute positioning of percentage
            borderRadius: "15px",
        },
        bar: {
            borderRadius: "5px",
            backgroundColor: pillbarColor,
        },
    })(LinearProgress);
};

export const renderTableCell = (data) => {
    // If the data is an object (JSON), render each key-value pair
    if (typeof data === "object" && data !== null) {
        return (
            <Table>
                <TableBody>
                    {Object.entries(data).map(([key, value]) => (
                        <TableRow key={key}>
                            <TableCell sx={{ padding: "1px!important;" }}>
                                {formatColumnName(key)}
                            </TableCell>
                            <TableCell sx={{ padding: "1px!important;" }}>
                                {renderTableCell(value)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    }
    if (typeof data === 'boolean') {
        return data ? 'True' : 'False'
    }
    return data;
};

export const DynamicDataTable = (Alert) => {
    // Parse JSON data and extract keys for table columns
    const columns = Object.keys(Alert);

    return (
        <TableContainer component={Paper}>
            <Table>
                <TableHead>
                    <TableRow>
                        {columns.map((column) => (
                            <TableCell
                                key={column}
                                sx={{
                                    padding: "0px!important;",
                                    color: "#7b809a !important;",
                                }}
                            >
                                {formatColumnName(column)}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        {columns.map((column) => (
                            <TableCell key={column}>
                                {renderTableCell(Alert[column])}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export function devStyle(isTabletOrMobile) {
    return {
        table: {
            padding: 0,
            borderSpacing: 0,
            borderCollapse: "collapse",
        },
        cell: {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: isTabletOrMobile ? "0" : "15px",
            paddingRight: isTabletOrMobile ? "0" : "15px",
            width: "56%",
        },
        cell1: {
            paddingTop: 0,
            paddingBottom: 0,
            paddingLeft: isTabletOrMobile ? "0" : "15px",
            paddingRight: isTabletOrMobile ? "0" : "15px",
            width: isTabletOrMobile ? "135px" : "175px",
        },
    }
}

const RenderTH = ({ value, styles }) => {
    return (
        <th scope="row" className="text-sm-end text-start" style={styles.cell1}>
            <MDTypography fontSize={1}>
                <span style={{ fontWeight: "700" }}>{value}:</span>
            </MDTypography>
        </th>
    );
};

const RenderTD = ({ value, style = null, fun = () => { } }) => {
    return (
        <td style={style} onClick={fun}>
            <MDTypography fontSize={1}>{value}</MDTypography>
        </td>
    );
};

export const RenderTR = ({ th, td, tdStyle: customTdStyle, tdFun, styles }) => {
    const mergedTdStyle = {
        ...customTdStyle,
        padding: "0px 15px",
    };

    return (
        <>
            {!["", null, undefined].includes(td) && (
                <>
                    <RenderTH value={th} styles={styles} />
                    <RenderTD value={td} style={mergedTdStyle} fun={tdFun} />
                </>
            )}
        </>
    );
};

const RenderTableRowData = ({ label, value, unit, renderType, styles }) => {
    return (
        <TableRow>
            <TableCell style={styles.cell}>
                <MDTypography fontSize={1}>
                    <span style={{ fontWeight: "700" }}>{label}:</span>
                </MDTypography>
            </TableCell>
            <TableCell style={styles.cell}>
                <MDTypography fontSize={1}>
                    {renderType === "text" ? (
                        typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) ? (
                            new Date(value).toLocaleDateString()
                        ) : isNaN(value) ? (
                            value
                        ) : (
                            `${Math.trunc(value)}${unit ? unit : ""}`
                        )
                    ) : renderType === "icon" ? (
                        <Box display="flex" alignItems="center">
                            <span>{value}</span>
                            &nbsp;
                            {renderIcon(label, value)}
                        </Box>
                    ) : (
                        <Box display="flex" alignItems="center">
                            <span>{value}</span>
                            {renderIcon(label, value)}
                        </Box>
                    )}
                </MDTypography>
            </TableCell>
        </TableRow>
    );
};

export const TableSection = ({ title, rowData, styles }) => (
    <Box sx={{ marginBottom: 2 }}>
        <MDTypography fontSize={1}>
            <span style={{ fontWeight: "700" }}>{title}</span>
        </MDTypography>
        <Table
            style={{
                ...styles.table,
                marginTop: "5px",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            }}
        >
            <TableBody>
                {rowData.map((row, idx) => (
                    <RenderTableRowData key={idx} {...row} styles={styles} />
                ))}
            </TableBody>
        </Table>
    </Box>
);